import { useEffect, useState, useRef, useCallback, lazy, Suspense } from 'react';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Style } from 'ol/style';
import Select from 'ol/interaction/Select';
import { pointerMove, click } from 'ol/events/condition';

import { useProject } from '@contexts/Project.context';
import { fetchUrl } from '@utils/api';
import { getLayerByCustomId, logError } from '@utils/map/helpers';
import { createPointStyle, createFootprintStyle } from '@utils/map/styles/droneImagesStyles';
import {
	POINTS_LAYER_ID,
	FOOTPRINTS_LAYER_ID,
	LAYER_NAME,
	SIDEBAR_ID,
	MIN_ZOOM_FOR_POINTS,
	DEFAULT_POINT_COLOR,
	DEFAULT_FOOTPRINT_COLOR,
	POINTS_LAYER_Z_INDEX_OFFSET,
} from '@config/droneImagesLayerConfig';
import { useGeoJSONData } from './hooks/useGeoJSONData';
import LayerSettings from '../../sidebars/sidebarElements/LayerSettings';

// Lazy load components that are not immediately needed
const DroneImagesSettings = lazy(() => import('./DroneImagesSettings'));
const ImageViewerModal = lazy(() => import('./ImageViewerModal'));

/**
 * Drone Images Layer Component
 * Shows drone image capture locations as points, with footprints appearing on hover
 * Click on a point to view the full image
 */
const DroneImagesLayer = ({
	layerConfig,
	visible = false,
	zIndex = 40,
	setDroneImagesSettingsActive,
}) => {
	const { mapObject, project, defaultFormat, removeFromActiveSidebars } = useProject();

	const pointsLayerRef = useRef(null);
	const footprintsLayerRef = useRef(null);
	const hoverInteractionRef = useRef(null);
	const clickInteractionRef = useRef(null);
	const footprintMapRef = useRef(new Map()); // O(1) lookup for matching footprints to points
	const highlightStyleRef = useRef(null);
	const abortControllerRef = useRef(null);
	const removeFromActiveSidebarsRef = useRef(removeFromActiveSidebars);

	const [isLayerVisible, setIsLayerVisible] = useState(visible);
	const [pointColor, setPointColor] = useState(DEFAULT_POINT_COLOR);
	const [footprintColor, setFootprintColor] = useState(DEFAULT_FOOTPRINT_COLOR);
	const [modalState, setModalState] = useState({
		selectedImage: null,
		imageUrl: null,
		isLoading: false,
	});

	const { uuid } = project || {};
	const imagesS3Prefix = layerConfig?.images_s3_prefix;

	// Keep ref updated with latest function
	useEffect(() => {
		removeFromActiveSidebarsRef.current = removeFromActiveSidebars;
	}, [removeFromActiveSidebars]);

	// Clean up when component unmounts
	useEffect(() => {
		return () => {
			// Remove sidebar using latest function reference
			removeFromActiveSidebarsRef.current(SIDEBAR_ID);
		};
	}, []);

	const { data: locationsData } = useGeoJSONData(
		'droneImageLocations',
		uuid,
		layerConfig?.image_locations_path,
		isLayerVisible
	);

	const { data: footprintsData } = useGeoJSONData(
		'droneImageFootprints',
		uuid,
		layerConfig?.grid_geojson_path,
		isLayerVisible
	);

	useEffect(() => {
		if (!mapObject) return;

		const existingLayer = getLayerByCustomId(mapObject, FOOTPRINTS_LAYER_ID);
		if (existingLayer) {
			footprintsLayerRef.current = existingLayer;
			return;
		}

		const layer = new VectorLayer({
			name: 'Drone Image Footprints',
			zIndex: zIndex,
			visible: false,
			source: new VectorSource({}),
			properties: { customLayerId: FOOTPRINTS_LAYER_ID },
			style: createFootprintStyle(footprintColor),
		});

		mapObject.addLayer(layer);
		footprintsLayerRef.current = layer;

		return () => {
			if (layer) mapObject.removeLayer(layer);
		};
	}, [mapObject, zIndex]);

	useEffect(() => {
		if (!mapObject) return;

		const existingLayer = getLayerByCustomId(mapObject, POINTS_LAYER_ID);
		if (existingLayer) {
			pointsLayerRef.current = existingLayer;
			return;
		}

		const layer = new VectorLayer({
			name: LAYER_NAME,
			zIndex: zIndex + POINTS_LAYER_Z_INDEX_OFFSET,
			visible: isLayerVisible,
			source: new VectorSource({}),
			properties: { customLayerId: POINTS_LAYER_ID },
			style: createPointStyle(pointColor),
			minZoom: MIN_ZOOM_FOR_POINTS,
		});

		mapObject.addLayer(layer);
		pointsLayerRef.current = layer;

		return () => {
			if (layer) mapObject.removeLayer(layer);
		};
	}, [mapObject, zIndex]);

	// Load footprints data
	useEffect(() => {
		const footprintsLayer = footprintsLayerRef.current;
		if (!footprintsLayer || !footprintsData) return;

		try {
			const crsName = footprintsData?.crs?.properties?.name || '';
			const isEpsg3857 = crsName.includes('3857');

			const features = defaultFormat.readFeatures(footprintsData, {
				dataProjection: isEpsg3857 ? 'EPSG:3857' : 'EPSG:4326',
				featureProjection: 'EPSG:3857',
			});

			footprintsLayer.setSource(new VectorSource({ features }));

			// Build O(1) lookup map for hover performance
			const map = new Map();
			features.forEach(f => {
				const tileName = f.get('tile_name');
				if (tileName) map.set(tileName, f);
			});
			footprintMapRef.current = map;
		} catch (err) {
			logError('DroneImages', 'Error loading footprints', err);
		}
	}, [footprintsData, defaultFormat]);

	// Load locations data
	useEffect(() => {
		const pointsLayer = pointsLayerRef.current;
		if (!pointsLayer || !locationsData) return;

		try {
			const crsName = locationsData?.crs?.properties?.name || '';
			const isEpsg3857 = crsName.includes('3857');

			const features = defaultFormat.readFeatures(locationsData, {
				dataProjection: isEpsg3857 ? 'EPSG:3857' : 'EPSG:4326',
				featureProjection: 'EPSG:3857',
			});

			pointsLayer.getSource().clear();
			pointsLayer.getSource().addFeatures(features);
		} catch (err) {
			logError('DroneImages', 'Error loading locations', err);
		}
	}, [locationsData, defaultFormat]);

	useEffect(() => {
		const pointsLayer = pointsLayerRef.current;
		if (pointsLayer) pointsLayer.setStyle(createPointStyle(pointColor));
		highlightStyleRef.current = createPointStyle(pointColor, true);
	}, [pointColor]);

	useEffect(() => {
		const footprintsLayer = footprintsLayerRef.current;
		if (footprintsLayer) footprintsLayer.setStyle(createFootprintStyle(footprintColor));
	}, [footprintColor]);

	// Setup hover interaction to show footprints on mouse over
	useEffect(() => {
		const pointsLayer = pointsLayerRef.current;
		const footprintsLayer = footprintsLayerRef.current;

		if (!mapObject || !pointsLayer || !footprintsLayer || !isLayerVisible) return;

		if (hoverInteractionRef.current) mapObject.removeInteraction(hoverInteractionRef.current);

		const selectInteraction = new Select({
			condition: pointerMove,
			layers: [pointsLayer],
			style: () => highlightStyleRef.current,
		});

		const handleSelect = e => {
			const selectedFeatures = e.selected;

			if (selectedFeatures.length > 0) {
				const tileName = selectedFeatures[0].get('tile_name');
				mapObject.getTargetElement().style.cursor = 'pointer';

				const footprint = footprintMapRef.current.get(tileName);
				if (footprint) {
					footprintsLayer
						.getSource()
						.getFeatures()
						.forEach(f => {
							f.setStyle(new Style({}));
						});
					footprint.setStyle(null);
					footprintsLayer.setVisible(true);
				}
			} else {
				mapObject.getTargetElement().style.cursor = 'auto';
				footprintsLayer.setVisible(false);
			}
		};

		selectInteraction.on('select', handleSelect);
		mapObject.addInteraction(selectInteraction);
		hoverInteractionRef.current = selectInteraction;

		return () => {
			if (selectInteraction) {
				selectInteraction.un('select', handleSelect);
				mapObject.removeInteraction(selectInteraction);
			}
			if (mapObject?.getTargetElement()) {
				mapObject.getTargetElement().style.cursor = 'auto';
			}
		};
	}, [mapObject, isLayerVisible, pointColor]);

	// Setup click interaction to open image modal
	useEffect(() => {
		const pointsLayer = pointsLayerRef.current;

		if (!mapObject || !pointsLayer || !isLayerVisible) return;

		if (clickInteractionRef.current) mapObject.removeInteraction(clickInteractionRef.current);

		const selectClick = new Select({
			condition: click,
			layers: [pointsLayer],
			style: () => highlightStyleRef.current,
		});

		const handleClickSelect = async e => {
			const selectedFeatures = e.selected;

			if (selectedFeatures.length > 0) {
				const tileName = selectedFeatures[0].get('tile_name');

				if (abortControllerRef.current) {
					abortControllerRef.current.abort();
				}

				const abortController = new AbortController();
				abortControllerRef.current = abortController;

				setModalState({
					selectedImage: tileName,
					imageUrl: null,
					isLoading: true,
				});

				if (imagesS3Prefix && tileName) {
					try {
						const imagePath = `${imagesS3Prefix}${tileName}`;
						const data = await fetchUrl(
							`fileLink?key=${imagePath}`,
							abortController.signal
						);

						if (abortController.signal.aborted) return;

						if (data?.url) {
							setModalState(prev => ({
								...prev,
								imageUrl: data.url,
								isLoading: false,
							}));
						} else {
							setModalState(prev => ({ ...prev, isLoading: false }));
						}
					} catch (err) {
						if (err.name === 'AbortError') return;
						logError('DroneImages', 'Error fetching image URL', err);
						if (!abortController.signal.aborted) {
							setModalState(prev => ({ ...prev, isLoading: false }));
						}
					}
				} else {
					setModalState(prev => ({ ...prev, isLoading: false }));
				}
			}

			selectClick.getFeatures().clear();
		};

		selectClick.on('select', handleClickSelect);
		mapObject.addInteraction(selectClick);
		clickInteractionRef.current = selectClick;

		return () => {
			if (selectClick) {
				selectClick.un('select', handleClickSelect);
				mapObject.removeInteraction(selectClick);
			}
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
				abortControllerRef.current = null;
			}
		};
	}, [mapObject, pointColor, isLayerVisible, imagesS3Prefix]);

	const handleCloseModal = useCallback(() => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
		}
		setModalState({
			selectedImage: null,
			imageUrl: null,
			isLoading: false,
		});
	}, []);

	const handleVisibilityChange = newVisible => {
		setIsLayerVisible(newVisible);
		const pointsLayer = pointsLayerRef.current;
		const footprintsLayer = footprintsLayerRef.current;

		if (pointsLayer) pointsLayer.setVisible(newVisible);
		if (!newVisible && footprintsLayer) footprintsLayer.setVisible(false);
	};

	if (!pointsLayerRef.current) return null;

	return (
		<>
			<li>
				<LayerSettings
					id={POINTS_LAYER_ID}
					layer={pointsLayerRef.current}
					label={LAYER_NAME}
					defaultState={isLayerVisible}
					onCheck={() => handleVisibilityChange(true)}
					onUncheck={() => handleVisibilityChange(false)}
					disabled={!pointsLayerRef.current}
					layerFunctions={[
						{
							title: 'Settings',
							onClick: setDroneImagesSettingsActive,
						},
					]}
				/>
				<Suspense fallback={null}>
					<DroneImagesSettings
						sidebarId={SIDEBAR_ID}
						pointColor={pointColor}
						setPointColor={setPointColor}
						footprintColor={footprintColor}
						setFootprintColor={setFootprintColor}
						totalImages={layerConfig?.total_images}
					/>
				</Suspense>
			</li>

			{/* Image Modal */}
			<Suspense fallback={null}>
				<ImageViewerModal
					show={!!modalState.selectedImage}
					imageName={modalState.selectedImage}
					imageUrl={modalState.imageUrl}
					isLoading={modalState.isLoading}
					onClose={handleCloseModal}
				/>
			</Suspense>
		</>
	);
};

export default DroneImagesLayer;
